import { Component, EventEmitter, OnDestroy, OnInit, Output, Input, inject } from "@angular/core";
import { LookerStudioService } from "../../shared/services/looker-studio.service";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { LoggerService } from "../../shared/services/logger.service";
import { TitleWrapperService } from "../../shared/services/title-wrapper.service";
import { SizeChangeEvent } from "../../shared/models/types";
import { CommonModule } from "@angular/common";
import { VFIconComponent } from "../../shared/component/vficon/vficon.component";
import { FormsModule } from "@angular/forms";
import { CollapseModule } from "ngx-bootstrap/collapse";
import { ReleasePhaseLabelComponent } from "../../shared/component/release-phase-label/release-phase-label.component";
import { map, mergeMap, switchMap } from "rxjs/operators";
import { AuthorisationService } from "../../shared/services/authorisation.service";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { BehaviorSubject, forkJoin, of, Subscription } from "rxjs";
import { MetricCollectionService } from "../../shared/services/metric-collection.service";
import { Csp, Page, Section } from "../../shared/models/vcloudsmart";
import { iframeSizes } from "../../shared/constant/customisation";
import { VCloudSmartApiService } from "../../shared/services/v-cloud-smart-api.service";
import { REGEX_VCS_URL } from "../../shared/constant/vcloud-api";

interface Parameters {
  csp: string;
  section: string;
  page: string;
  version: string;
}

@Component({
  imports: [VFIconComponent, FormsModule, CollapseModule, RouterModule, ReleasePhaseLabelComponent, TooltipModule],
  selector: "app-looker-studio-navigation",
  templateUrl: "./looker-studio-navigation.component.html",
  styleUrls: ["./looker-studio-navigation.component.scss"],
})
export class LookerStudioNavigationComponent implements OnInit, OnDestroy {
  private log = inject(LoggerService);
  private metrics = inject(MetricCollectionService);
  private route = inject(ActivatedRoute);
  private looker = inject(LookerStudioService);
  private vcs = inject(VCloudSmartApiService);
  private title = inject(TitleWrapperService);
  router = inject(Router);
  private authorisation = inject(AuthorisationService);
  private authentication = inject(AuthenticationService);
  private readonly savedRatio: string | null = localStorage.getItem("vcsRatio");

  @Output()
  public sizeChanged: BehaviorSubject<SizeChangeEvent> = new BehaviorSubject({
    text: this.savedRatio && Object.keys(iframeSizes).includes(this.savedRatio) ? this.savedRatio : "full width",
    value: this.savedRatio && Object.keys(iframeSizes).includes(this.savedRatio) ? iframeSizes[this.savedRatio] : 1,
  });

  public timeouts: number[] = [];

  private subscriptions: Subscription[] = [];
  public urlParams: Partial<Parameters> = {};
  public reportState?: {
    csp: Csp;
    section: Section;
    page: Page;
  };

  public Object = Object;

  public iframeSizesKeys = Object.keys(iframeSizes);
  public iframeSizes: { [key: string]: number } = iframeSizes;
  public vcsConfig: Array<Csp> = [];

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.timeouts.forEach((s) => clearTimeout(s));
  }

  public get currentTenant(): string {
    return this.authentication.tenant;
  }

  public get currentSize(): string {
    return this.sizeChanged.getValue().text;
  }

  public handleSizeChange(sizeName: string): void {
    if (this.currentSize !== sizeName) {
      this.metrics.reportWidthChanged(this.router.url, sizeName);
      this.sizeChanged.next({ text: sizeName, value: this.iframeSizes[sizeName] });
      window.dispatchEvent(new Event("resize"));
      localStorage.setItem("vcsRatio", sizeName);
    }
  }

  private getParams(_params: { [k: string]: any }): Partial<Parameters> {
    if (_params === null || _params === undefined) {
      return this.getParams({});
    }
    if (location.pathname === "/vcloudsmart/assistant") {
      return {};
    }
    const params = { ..._params };
    const segments: string[] = (this.router?.url ?? "")
      .split("?")[0]
      .replace(REGEX_VCS_URL, "")
      .split("/")
      .filter(Boolean);
    const slices: string[] = segments.slice(0, 3);
    const sl: number = slices?.length ?? 0;
    if (!params.csp && sl >= 1) {
      params.csp = slices[0];
    }
    if (!params.section && sl >= 2) {
      params.section = slices[1];
    }
    if (!params.page && sl >= 3) {
      params.page = slices[2];
    }
    if (!params.version) {
      const versionMatch: RegExpExecArray = REGEX_VCS_URL.exec(this.router.url);
      if (versionMatch && versionMatch.length >= 2) {
        params.version = versionMatch[1].slice(1);
      }
    }
    for (const k in params) {
      params[k] = decodeURIComponent(params[k]);
    }
    return params as Parameters;
  }

  ngOnInit(): void {
    this.log.warning("initialising looker studio navigation component");

    this.subscriptions.push(
      this.route.params
        .pipe(
          map((routeParams) => {
            this.urlParams = this.getParams(routeParams);
            this.log.info({ params: this.urlParams });
            this.title.setVcsTitle();
          }),
          switchMap(() => {
            return forkJoin({
              user: this.authorisation.getUserPermissions({
                attributes: ["allowed_tenants", "current_tenant", "groups", "permissions", "tenancies"],
              }),
              customReports: this.vcs.getCustomReports(),
            });
          }),
          mergeMap(({ user, customReports }) => {
            return forkJoin({
              user: of(user),
              customReports: of(customReports),
              vcsConfig: this.looker.getVCSConfigV3(this.currentTenant, true, { userInfo: user, customReports }),
            });
          }),
        )
        .subscribe(({ user, vcsConfig }) => {
          this.vcsConfig = vcsConfig;
          const csp: string = this.urlParams.csp ?? "";
          const section: string = this.urlParams.section ?? "";
          const page: string = this.urlParams.page ?? "";
          this.reportState = this.looker.getParams(this.currentTenant, csp, section, page, user);
          // Currency initialization is now handled by CurrencyResolver
          // No need to call handleCurrencyChange here on route changes
        }),
    );
  }

  public getReportLink(csp: Csp, section: Section, page: Page): string {
    return this.looker.getReportLink(csp, section, page);
  }

  public get migratedVersionSelected(): boolean {
    return this.urlParams?.version === "v2";
  }

  public get isCurrentPageMigrated(): boolean {
    return !!this.reportState?.page?.isMigrated;
  }

  public get userVersionPreferences(): { [key: string]: boolean } {
    return this.looker.getUserSelectedPagesVersion();
  }

  public isActive(csp: Csp, section: Section, page: Page): boolean {
    const tmpActiveRoutParams = this.getParams(this.route.params);
    return (
      tmpActiveRoutParams?.csp === csp?.id &&
      tmpActiveRoutParams?.section === section?.title &&
      tmpActiveRoutParams?.page === page?.title
    );
  }

  public onVersionChange(): void {
    const csp: string = this.urlParams.csp;
    const section: string = this.urlParams.section;
    const page: string = this.urlParams.page;
    if (!csp || !section || !page) {
      return;
    }

    const isMigrated = !this.migratedVersionSelected;
    this.looker.setUserSelectedPagesVersion(csp, section, page, isMigrated);
    if (isMigrated) {
      this.router.navigate(["/vcloudsmart/v2", csp, section, page]);
    } else {
      this.metrics.reportSwitchedToLegacy(this.router.url);
      this.router.navigate(["/vcloudsmart", csp, section, page]);
    }
  }

  protected readonly location = location;

  public get isCustomReport(): boolean {
    const s: string = location?.pathname || "";
    return s.includes("/custom-reports/") && !!s.split("/").slice(-1)[0];
  }
}
